import type { CategoryName } from './types';

/** The Google Consent Mode v2 signals this package controls. */
export type ConsentModeSignal =
  | 'ad_storage'
  | 'ad_user_data'
  | 'ad_personalization'
  | 'analytics_storage'
  | 'functionality_storage'
  | 'personalization_storage'
  | 'security_storage';

export type ConsentModeState = Record<ConsentModeSignal, 'granted' | 'denied'>;

/** Which category grants which signal. `security_storage` is always granted (strictly necessary). */
const SIGNALS_BY_CATEGORY: Record<Exclude<CategoryName, 'necessary'>, ConsentModeSignal[]> = {
  functional: ['functionality_storage', 'personalization_storage'],
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
};

/** Map accepted categories to a full Consent Mode state. Anything not accepted is denied. */
export function toConsentMode(accepted: readonly string[]): ConsentModeState {
  const state: ConsentModeState = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  };
  for (const [category, signals] of Object.entries(SIGNALS_BY_CATEGORY)) {
    if (accepted.includes(category)) {
      for (const signal of signals) state[signal] = 'granted';
    }
  }
  return state;
}

type Gtag = (...args: unknown[]) => void;

interface GtagWindow {
  dataLayer?: unknown[];
  gtag?: Gtag;
}

function gtag(): Gtag {
  const w = window as unknown as GtagWindow;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== 'function') {
    // Same shape as Google's snippet: gtag pushes its `arguments` object onto the dataLayer.
    w.gtag = function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      (w.dataLayer as unknown[]).push(arguments);
    };
  }
  return w.gtag;
}

/**
 * Deny everything before any Google tag runs. Call it as early as possible;
 * the CDN build calls it synchronously when the script loads.
 */
export function setConsentModeDefaults(): void {
  gtag()('consent', 'default', { ...toConsentMode([]), wait_for_update: 500 });
  gtag()('set', 'ads_data_redaction', true);
}

export function updateConsentMode(accepted: readonly string[]): void {
  gtag()('consent', 'update', toConsentMode(accepted));
}

/**
 * Inline snippet for server-rendered <head> (Next.js, PHP …) so defaults are set
 * before GTM even when the package bundle loads later.
 */
export function consentModeDefaultsSnippet(): string {
  const defaults = JSON.stringify({ ...toConsentMode([]), wait_for_update: 500 });
  return (
    'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}' +
    `gtag('consent','default',${defaults});gtag('set','ads_data_redaction',true);`
  );
}
